import { Bot, MessageSquare, Play, X } from "lucide-react";
import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useAuth } from "@/providers";
import { useAgents } from "@/shared/domain/agents/hooks";
import { useSlackOutboundChannels } from "@/shared/domain/slack/hooks";
import { TriggerTargetType } from "@/shared/domain/triggers";
import {
  Card,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";

import { TARGET_KINDS } from "../formScope";
import {
  type TargetFormValues,
  type TriggerFormValues,
} from "../triggerFormSchema";

import { InvokeActionTargetCard } from "./invokeAction/InvokeActionTargetCard";
import { SendSlackMessageTargetCard } from "./sendSlackMessage/SendSlackMessageTargetCard";
import { StartAgentTargetCard } from "./startAgent/StartAgentTargetCard";
import styles from "./targets.module.css";

interface KindCopy {
  label: string;
  description: string;
  icon: React.ReactNode;
  /** The card's summary once the target names what it acts on. */
  summary: (chosen: string) => string;
}

const KIND_COPY: Record<TriggerTargetType, KindCopy> = {
  [TriggerTargetType.InvokeAction]: {
    label: "Invoke an action",
    description: "Run an action on what changed.",
    icon: <Play aria-hidden size={16} />,
    summary: (action) => `Invoke ${action}`,
  },
  [TriggerTargetType.StartAgent]: {
    label: "Start an agent",
    description: "Start an agent on what changed.",
    icon: <Bot aria-hidden size={16} />,
    summary: (agent) => `Start ${agent}`,
  },
  [TriggerTargetType.SendSlackMessage]: {
    label: "Post to Slack",
    description: "Send a message about what changed.",
    icon: <MessageSquare aria-hidden size={16} />,
    summary: (channel) => `Post to ${channel}`,
  },
};

export interface TargetCardProps {
  index: number;
  onRemove: () => void;
  /** Replaces this target with a fresh one of `kind`, keeping its position. */
  onChangeKind: (kind: TriggerTargetType) => void;
}

/**
 * One target: the kind it is, and the fields that kind needs.
 *
 * The list owns which targets exist; the card owns the fields of its own.
 */
export function TargetCard({ index, onRemove, onChangeKind }: TargetCardProps) {
  const { control } = useFormContext<TriggerFormValues>();
  const target = useWatch<TriggerFormValues>({
    control,
    name: `targets.${index}`,
  }) as TargetFormValues;
  const kind = target.type;

  return (
    <Card.Root
      size="compact"
      data-testid={`trigger-target-${index}`}
      data-kind={kind}
    >
      <Card.Header>
        {/* The card's title in the page outline; the parts under it are h4s. */}
        <h3 className={styles.targetHeading}>
          <Card.Title>Target</Card.Title>{" "}
          <Card.Summary>
            <TargetSummary target={target} />
          </Card.Summary>
        </h3>
        <Card.Actions>
          <RobotoButton
            variant="ghost"
            icon={<X aria-hidden />}
            aria-label="Remove this target"
            onClick={onRemove}
            data-testid={`trigger-target-${index}-remove`}
          />
        </Card.Actions>
      </Card.Header>
      <Card.Body className={styles.targetBody}>
        <RadioGroup<TriggerTargetType>
          appearance="card"
          size="md"
          aria-label="What this target does"
          value={kind}
          onValueChange={onChangeKind}
        >
          {TARGET_KINDS.map((offered) => (
            <Radio
              key={offered}
              value={offered}
              label={KIND_COPY[offered].label}
              description={KIND_COPY[offered].description}
              icon={KIND_COPY[offered].icon}
            />
          ))}
        </RadioGroup>

        {kind === TriggerTargetType.InvokeAction && (
          <InvokeActionTargetCard index={index} />
        )}
        {kind === TriggerTargetType.StartAgent && (
          <StartAgentTargetCard index={index} />
        )}
        {kind === TriggerTargetType.SendSlackMessage && (
          <SendSlackMessageTargetCard index={index} />
        )}
      </Card.Body>
    </Card.Root>
  );
}

/**
 * One line naming what the target does, and what it acts on once that is
 * chosen. The form holds an agent or channel by id, so its name comes from the
 * same query its picker reads: no fetch of its own, and none on a target of
 * another kind.
 */
function TargetSummary({ target }: { target: TargetFormValues }) {
  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return targetSummary(target.type, target.spec.action?.name ?? null);
    case TriggerTargetType.StartAgent:
      return <AgentSummary agentId={target.agentId} />;
    case TriggerTargetType.SendSlackMessage:
      return <ChannelSummary channelId={target.channelId} />;
  }
}

function AgentSummary({ agentId }: { agentId: string | null }) {
  const { currentOrganization } = useAuth();
  const agentsQuery = useAgents({ orgId: currentOrganization?.org_id });
  const chosen = agentsQuery.data?.items.find(
    (agent) => agent.agent_id === agentId,
  );
  return targetSummary(TriggerTargetType.StartAgent, chosen?.name ?? null);
}

function ChannelSummary({ channelId }: { channelId: string | null }) {
  const { currentOrganization } = useAuth();
  const channelsQuery = useSlackOutboundChannels(currentOrganization?.org_id);
  const chosen = channelsQuery.data?.find(
    (channel) => channel.channel_id === channelId,
  );
  return targetSummary(
    TriggerTargetType.SendSlackMessage,
    chosen === undefined ? null : `#${chosen.channel_name}`,
  );
}

function targetSummary(kind: TriggerTargetType, chosen: string | null): string {
  return chosen === null
    ? KIND_COPY[kind].label
    : KIND_COPY[kind].summary(chosen);
}
